<template>
  <div>
    <div class="s-content-block s-content-block--small">
      <div
        v-bind:class="{
          'o-form': true,
          'o-form--course-search': true,
          'o-form--sidebar': isOnResultsPage,
        }"
      >
        <h4 v-if="isOnResultsPage">Course to Course Search</h4>
        <div class="o-form__section s-content-block s-content-block--small" id="course-information">
          <h4>Course Information*</h4>

          <!-- From or To -->
          <div
            class="m-field-group m-field-group--contain m-button-radio-group"
            id="from-to-institution"
          >
            <div>
              <input
                type="radio"
                id="from-institution"
                value="from"
                name="transfer-type"
                v-model="filter.fromOrToInstitution"
                @click="fromToChange"
              />
              <label for="from-institution">From this Institution</label>
            </div>
            <div>
              <input
                type="radio"
                id="to-institution"
                value="to"
                name="transfer-type"
                v-model="filter.fromOrToInstitution"
                @click="fromToChange"
              />
              <label for="to-institution">To this Institution</label>
            </div>
          </div>

          <!-- Country -->
          <div class="m-field-group">
            <label for="countrySelect">Country<sup>*</sup></label>
            <multiselect
              v-if="filter.fromOrToInstitution == 'from'"
              id="countrySelect"
              v-model="filter.selectedCountry"
              @select="countrySelected"
              :options="countries"
              :multiple="false"
              :custom-label="countryMultiselectCustomLabel"
              track-by="id"
              :placeholder="'Select Country'"
              :preselect-first="false"
              :showLabels="false"
              :disabled="!countries || !countries.length"
              :loading="!countries || !countries.length"
              autocomplete="off"
            >
              <template slot="option" slot-scope="props">
                <span :title="countryMultiselectCustomLabel(props.option)">{{
                  countryMultiselectCustomLabel(props.option)
                }}</span>
              </template>
            </multiselect>

            <div v-else>
              <input
                type="text"
                value="BC (including Yukon University and Athabasca University)"
                aria-label="BC (including Yukon University and Athabasca University)"
                title="BC (including Yukon University and Athabasca University)"
                readonly
                disabled
                class="disabled-country-input"
              />
            </div>
          </div>

          <!-- Institution -->
          <div class="m-field-group">
            <label for="institutionSelect">Institution<sup>*</sup></label>
            <multiselect
              id="institutionSelect"
              v-model="filter.selectedInstitution"
              @select="institutionSelected"
              :options="institutions"
              :multiple="false"
              :custom-label="multiselectInstitutionCustomLabel"
              track-by="id"
              :placeholder="institutionPlaceholder"
              :preselect-first="false"
              :showLabels="false"
              autocomplete="off"
              :disabled="institutionsLoading || !institutionsInitiallyLoaded"
              :loading="institutionsLoading || !institutionsInitiallyLoaded"
            >
              <template #option="props">
                <div class="option__desc">
                  <!-- Shield -->
                  <img
                    v-if="props.option.isMember"
                    class="option__image"
                    :src="shieldCheckIcon"
                    width="16"
                    height="16"
                  />
                  <img
                    v-else
                    class="option__image"
                    width="16"
                    height="16"
                    :style="{ opacity: 0 }"
                  />

                  <!-- Label -->
                  <span
                    class="option__title"
                    :title="multiselectInstitutionCustomLabel(props.option)"
                    v-html="multiselectInstitutionCustomLabel(props.option)"
                  ></span>
                </div>
              </template>
            </multiselect>
            <small class="a-note"
              ><img class="option__image" :src="props.shieldCheckIcon" /> BC Transfer System Member.
              For more info, see
              <a href="https://bccat.ca/system" rel="noopener" target="_blank">BC Transfer System</a
              >.</small
            >
          </div>

          <!-- Subject -->
          <div class="m-field-group">
            <label for="subjectSelect">
              Subject
              <sup
                v-if="
                  filter.selectedInstitution &&
                  !filter.selectedInstitution.isMember &&
                  currentPossibleSubjects.length > 0
                "
              >
                *
              </sup>
            </label>

            <multiselect
              id="subjectSelect"
              ref="subjectSelect"
              v-model="filter.selectedSubject"
              :options="currentPossibleSubjects"
              :multiple="false"
              :custom-label="multiselectSubjectCustomLabel"
              track-by="id"
              :placeholder="subjectPlaceholder"
              :preselect-first="false"
              :showLabels="false"
              :disabled="
                subjectsLoading ||
                !subjectsInitiallyLoaded ||
                !currentPossibleSubjects ||
                !currentPossibleSubjects.length
              "
              :loading="
                filter.selectedInstitution &&
                (subjectsLoading ||
                  !subjectsInitiallyLoaded ||
                  !currentPossibleSubjects ||
                  !currentPossibleSubjects.length)
              "
              @select="onSubjectChanged"
            >
              <template #option="props">
                <span :title="multiselectSubjectCustomLabel(props.option)">{{
                  multiselectSubjectCustomLabel(props.option)
                }}</span>
              </template>
            </multiselect>

            <!-- <div v-if="showSubjectValidation" style="color: #ff0000; padding-top: 5px">
              Please select a Subject. Course Load searches do not allow “any” Subject.
            </div> -->
          </div>

          <!-- Course Number -->
          <div class="m-field-group">
            <label for="courseNumber">Course Number</label>
            <input
              id="courseNumber"
              type="text"
              v-model="filter.courseNumber"
              maxlength="10"
              placeholder="e.g. “101” or “1” for any 100 levels"
              :disabled="!filter.selectedInstitution || !filter.selectedSubject"
              @input="trimInput"
            />
            <div v-if="showCourseNumberSpaceError" style="color: #ff0000; padding-top: 5px">
              Course Number can't contain the space character.
            </div>
          </div>

          <!-- Year -->
          <fieldset
            class="o-form__section s-content-block s-content-block--small"
            id="year-completed"
          >
            <label>Year Course Completed</label>
            <div class="m-field-group m-field-group--contain" id="YearSelect">
              <year-select-component
                ref="yearSelectComponentRef"
                @change="yearChanged"
              ></year-select-component>
            </div>
          </fieldset>

          <!-- Add Course Button -->
          <div class="m-field-group m-field-group--contain text-right">
            <div
              class="c2c-add-course-tooltip"
              data-tooltip='Choose "Add Course" and input other courses to run a multi-course search.'
              style="position: relative !important"
            >
              i
            </div>
            <button
              id="add-more-btn"
              type="button"
              class="a-button a-button--secondary-outline a-button--small"
              @click="addCourse"
              :disabled="
                !(filter.selectedInstitution && filter.selectedSubject && filter.courseNumber) ||
                showSubjectValidation
              "
              v-bind="addCourseTooltip ? { 'data-tooltip': addCourseTooltip } : {}"
            >
              Add Course +
            </button>
          </div>

          <div class="m-selected-items">
            <div class="a-selected-item" v-for="course in filter.selectedCourses" :key="course.id">
              <span v-if="course.courseYear == '1999'"
                >{{ course.institutionCode }} {{ course.subjectCode }} {{ course.courseNumber }} -
                1995-1999</span
              >
              <span v-else-if="course.courseYear == '2005'"
                >{{ course.institutionCode }} {{ course.subjectCode }} {{ course.courseNumber }} -
                2000-2005</span
              >
              <span v-else-if="course.courseYear == '2010'"
                >{{ course.institutionCode }} {{ course.subjectCode }} {{ course.courseNumber }} -
                2006-2010</span
              >
              <span v-else-if="course.courseYear == null"
                >{{ course.institutionCode }} {{ course.subjectCode }} {{ course.courseNumber }} -
                (1995 onwards)</span
              >
              <span v-else
                >{{ course.institutionCode }} {{ course.subjectCode }} {{ course.courseNumber }} -
                {{ course.courseYear + '/' + _.toString(course.courseYear + 1).slice(-2) }}</span
              >
              <span class="fa fa-close" @click="removeCourse(course)"></span>
            </div>
          </div>

          <!-- From / To Institution -->
          <h4 class="from-to-institution-filters">
            <span v-if="filter.fromOrToInstitution == 'to'">From</span>
            <span v-else>To</span>
            Institution
          </h4>

          <fieldset
            class="o-form__section s-content-block s-content-block--small from-to-institution-country-filter"
          >
            <institution-multiselect-component
              ref="secondInstitutionsRef"
              :nonce="nonce"
              :search="filter.fromOrToInstitution"
              :countries="countries"
              :filter="filter"
              :searchCustomBaseURL="props.apiUrls.searchCustomBaseURL"
              @institutionsChanged="secondInstitutionsChanged"
              @institutionTypesChanged="secondInstitutionTypesChanged"
              @privateInstitutionTypeChanged="secondPrivateInstitutionTypeChanged"
              @countryChanged="secondCountryChanged"
              @institutionsForSenderorReceiversChanged="
                secondInstitutionsForSenderorReceiversChanged
              "
              :is-on-results-page="isOnResultsPage"
            ></institution-multiselect-component>
          </fieldset>

          <!-- Submit -->
          <button
            :disabled="
              !filter.selectedInstitution ||
              (!filter.selectedInstitution.isMember &&
                currentPossibleSubjects.length > 0 &&
                !filter.selectedSubject) ||
              showValidation
            "
            id="search-submit"
            class="a-button"
            @click="submitSearch"
          >
            Search
          </button>

          <div v-if="showValidation" style="color: #ff0000; padding-top: 10px">
            There is no course-to-course transfer from your selected country. Please select a
            different Country in the From Institution section.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
$(window).keypress(function (e) {
  var keyCode = e.which
  if (keyCode == 13 && !$('#search-submit').is(':disabled')) {
    $('#search-submit').trigger('click')
  }
})

import { defineProps, defineEmits, ref, onMounted, computed, defineExpose } from 'vue'
import axios from 'axios'
import YearSelectComponent from './YearSelectComponent.vue'
import InstitutionMultiselectComponent from './InstitutionMultiselectComponent.vue'
import Multiselect from 'vue-multiselect'
import _ from 'lodash'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'

const fromCourseToCourseSearchFilterDataStorageName = 'from-course-to-course-search-params'
const toCourseToCourseSearchFilterDataStorageName = 'to-course-to-course-search-params'

const emit = defineEmits(['onSubmit'])

const props = defineProps({
  nonce: {
    required: true,
  },
  basePagePath: {
    default: false,
  },
  resultsPagePath: {
    default: false,
  },
  isOnResultsPage: {
    default: false,
  },
  shieldCheckIcon: {
    required: true,
  },
  apiUrls: {
    type: Object,
    required: true,
  },
  search: {
    default: 'from',
  },
})

const secondInstitutionsRef = ref(null)
const yearSelectComponentRef = ref(null)
const tooltip = ref(null)
const showSubjectValidation = ref(false)

const filter = ref({
  fromOrToInstitution: 'from',
  selectedCountry: null,
  selectedInstitution: null,
  selectedSubject: null,
  courseNumber: null,
  courseYear: null,
  selectedSecondInstitutionTypes: null,
  selectedSecondPrivateInstitutionType: null,
  selectedSecondInstitutions: null,
  secondInstitutionsForSenderorReceivers: null,
  defaultCountry: {
    id: 'BC',
    countryName: 'BC (including Yukon University and Athabasca University)',
  },
  selectedSecondCountry: {
    id: 'BC',
    countryName: 'BC (including Yukon University and Athabasca University)',
  },
  selectedCourses: [],
})

const countryBC = ref(null)
const searchDirection = ref('')
const countries = ref([])
const countryCanada = ref(null)
const countryUSA = ref(null)
const institutions = ref([])
const institutionsLoading = ref(false)
const institutionsInitiallyLoaded = ref(false)
const allSubjects = ref({})
const subjectsObservable = ref(null)
const subjectsLoading = ref(false)
const subjectsInitiallyLoaded = ref(true)
const currentPossibleSubjects = ref([])
const areDetailsParamsUsedForInitialLoad = ref(false)
const showValidation = ref(false)
const showCourseNumberSpaceError = ref(false)

const addCourseTooltip = computed(() => {
  if (showSubjectValidation.value) {
    return 'Please select a Subject. Course Load searches do not allow "any" Subject.'
  }

  if (
    !filter.value.selectedInstitution ||
    !filter.value.selectedSubject ||
    !filter.value.courseNumber
  ) {
    return 'Define Institution, Subject and Course Number to add to the Course Load search.'
  }
  return ''
})

const institutionPlaceholder = computed(() => {
  if (institutionsLoading.value) {
    return 'Loading Institutions ..'
  }
  if (!institutionsInitiallyLoaded.value) {
    return 'Select an Institution'
  }
  return 'Select an Institution'
})

const subjectPlaceholder = computed(() => {
  if (subjectsLoading.value) {
    return 'Loading Subjects ..'
  }
  if (!subjectsInitiallyLoaded.value) {
    return 'Select a Subject'
  }
  return 'Select a Subject'
})

const urlParams = new URLSearchParams(window.location.search)
const fromTo = urlParams.get('fromTo')
const storedValue = sessionStorage.getItem('searchDirection')
if (storedValue) {
  searchDirection.value = storedValue
} else {
  searchDirection.value = 'from'
}
switch (fromTo) {
  case 'from':
  case 'to':
    filter.value.fromOrToInstitution = fromTo
    break
  default:
    filter.value.fromOrToInstitution = searchDirection.value
    break
}

const trimInput = (event) => {
  const value = event.target.value
  if (value.includes(' ')) {
    filter.value.courseNumber = value.replace(/\s+/g, '')
    showCourseNumberSpaceError.value = true
  } else {
    filter.value.courseNumber = sanitizeInput(value)
    showCourseNumberSpaceError.value = false
  }
}

const sanitizeInput = (input) => {
  return input.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, function (char) {
    switch (char) {
      case '\0':
        return '\\0'
      case '\x08':
        return '\\b'
      case '\x09':
        return '\\t'
      case '\x1a':
        return '\\z'
      case '\n':
        return '\\n'
      case '\r':
        return '\\r'
      case '"':
      case "'":
      case '\\':
      case '%':
        return '\\' + char
    }
  })
}

const countrySelected = (country) => {
  filter.value.selectedInstitution = null
  filter.value.selectedSubject = null
  currentPossibleSubjects.value = []
  filter.value.courseNumber = null
  fetchInstitutions(country.id).then((fetchedInstitutions) => {
    institutions.value = fetchedInstitutions
  })
}

const institutionSelected = (institution) => {
  filter.value.selectedInstitution = institution
  filter.value.selectedSubject = null
  filter.value.courseNumber = null
  currentPossibleSubjects.value = []
  fetchSubjects(institution.id).then((subjects) => {
    currentPossibleSubjects.value = subjects
  })
}

const fetchCountries = () => {
  return new Promise((resolve, reject) => {
    const updateCountries = (countries) => {
      countryBC.value = _.first(
        _.filter(
          countries,
          (country) =>
            _.get(country, 'countryName') ===
            'BC (including Yukon University and Athabasca University)',
        ),
      )
      countryCanada.value = _.first(
        _.filter(countries, (country) => _.get(country, 'countryName') === 'Canada'),
      )
      countryUSA.value = _.first(
        _.filter(
          countries,
          (country) => _.get(country, 'countryName') === 'United States of America',
        ),
      )

      if (!filter.value.selectedCountry) {
        filter.value.selectedCountry = countryBC.value
      }
      return countries
    }
    let countries = sessionStorage.getItem('courseToCourseCountries')
    if (_.isString(countries)) {
      countries = JSON.parse(countries)
    }

    if (countries) {
      countries = updateCountries(countries)
      resolve(countries)
      return
    }

    axios
      .get(props.apiUrls.searchCustomBaseURL + 'agreementws/GetCountries')
      .then((response) => {
        var canada = response.data.filter((el) => el.countryShortName == 'Canada')
        var usa = response.data.filter((el) => el.countryShortName == 'USA')
        let bc = JSON.parse(JSON.stringify(canada))
        bc[0].id = 'BC'
        bc[0].countryName = 'BC (including Yukon University and Athabasca University)'
        bc[0].countryShortName = 'BC'
        bc[0].alpha2 = 'BC'
        bc[0].alpha3 = 'BC'
        response.data.unshift(usa[0])
        response.data.unshift(canada[0])
        response.data.unshift(bc[0])
        const countries = updateCountries(response.data)
        sessionStorage.setItem('courseToCourseCountries', JSON.stringify(countries))
        resolve(countries)
      })
      .catch((error) => {
        console.error('Error fetching countries:', error)
        reject(error)
      })
  })
}

const fromToChange = (event) => {
  const value = event.target.value
  filter.value.fromOrToInstitution = value
  filter.value.selectedCountry = null
  filter.value.selectedCourses = []
  showSubjectValidation.value = false

  filter.value.selectedSecondCountry = filter.value.defaultCountry

  // reset to default
  secondInstitutionsRef.value.setCountry(filter.value.defaultCountry)

  restorePersistence()
}

const fetchInstitutions = (countryId) => {
  return new Promise((resolve, reject) => {
    institutionsLoading.value = true

    if (!countryId) {
      countryId = _.get(filter.value, 'selectedCountry.id', countryBC.value.id)
    }

    // secondInstitutionsRef.value.setInstitutionCountryChange(filter.value.selectedSecondCountry.id)

    const storageKey = `courseToCourseInstitutions${countryId}`
    let institutions = sessionStorage.getItem(storageKey)
    if (_.isString(institutions)) {
      institutions = JSON.parse(institutions)
      institutions.value = institutions
      institutionsLoading.value = false
      institutionsInitiallyLoaded.value = true
      resolve(institutions)
      return
    }

    axios
      .get(props.apiUrls.searchCustomBaseURL + 'agreementws/GetFromInstitutions', {
        params: {
          countryId: countryId == 'BC' ? 40 : countryId,
          internalOnly: countryId == 'BC' ? 'true' : 'false',
        },
      })
      .then((response) => {
        const institutions = response.data
        institutions.value = institutions
        sessionStorage.setItem(storageKey, JSON.stringify(institutions))
        institutionsLoading.value = false
        institutionsInitiallyLoaded.value = true
        resolve(institutions)
      })
  })
}

const fetchSubjects = (institutionId) => {
  return new Promise((resolve, reject) => {
    subjectsLoading.value = true
    let existingSubjects = sessionStorage.getItem(
      `courseToCourseSubjectsForInstitution${institutionId}${filter.value.fromOrToInstitution}`,
    )
    if (_.isString(existingSubjects)) {
      existingSubjects = JSON.parse(existingSubjects)
    }
    if (existingSubjects && existingSubjects.length > 0) {
      if (!filter.value.selectedInstitution.isMember) {
        let any = { id: 0, title: 'Any' }
        existingSubjects.unshift(any)
      }
      subjectsLoading.value = false
      subjectsInitiallyLoaded.value = true
      resolve(existingSubjects)
      return
    }
    axios
      .get(props.apiUrls.searchCustomBaseURL + 'agreementws/GetSubjects', {
        params: {
          institutionID: institutionId,
          sending: filter.value.fromOrToInstitution == 'from' ? true : false,
        },
      })
      .then((response) => {
        const subjects = response.data
        sessionStorage.setItem(
          `courseToCourseSubjectsForInstitution${institutionId}${filter.value.fromOrToInstitution}`,
          JSON.stringify(subjects),
        )

        if (!filter.value.selectedInstitution.isMember) {
          let any = { id: 0, title: 'Any' }
          subjects.unshift(any)
        }
        subjectsLoading.value = false
        subjectsInitiallyLoaded.value = true
        resolve(subjects)
      })
  })
}

const countryMultiselectCustomLabel = (data) => {
  if (data.countryShortName) {
    return _.get(data, 'countryShortName')
  } else {
    return _.get(data, 'countryName')
  }
}

const multiselectInstitutionCustomLabel = (data) => {
  const code = _.get(data, 'code')
  const title = _.get(data, 'title')
  const country = _.get(data, 'country')
  const countryShortName = _.get(data, 'countryShortName')
  const province = _.get(data, 'province')
  let label = ''
  if (code && title) {
    label = `${code} - ${title}`
  } else if (code) {
    label = `${code}`
  }
  if (['Canada', 'United States of America'].includes(country) && province) {
    label = `${label} (${province})`
  } else if (countryShortName) {
    label = `${label} (${countryShortName})`
  } else if (country) {
    label = `${label} (${country})`
  }
  return label
}

const multiselectSubjectCustomLabel = (data) => {
  const code = _.get(data, 'code')
  const title = _.get(data, 'title')
  let label = ''
  if (code && title) {
    label = `${code} - ${title}`
  } else if (code) {
    label = `${code}`
  }
  if (title == 'Any') {
    label = `${title}`
  }
  return label
}

const clearStorage = () => {
  for (var storageKey in sessionStorage) {
    if (
      storageKey.startsWith('courseToCourseSubjectsForInstitution') ||
      storageKey.startsWith('courseToCourseInstitutions')
    ) {
      sessionStorage.removeItem(storageKey)
    }
  }
  sessionStorage.removeItem('courseToCourseCountries')
}

const areDetailsParamsSet = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const institutionId = _.toInteger(urlParams.get('iid'))
  return institutionId
}

const removeInstitutionTag = (course) => {
  removeCourse(course)
  submit()
}

const updatePersistence = () => {
  if (filter.value.fromOrToInstitution === 'from') {
    sessionStorage.setItem(
      fromCourseToCourseSearchFilterDataStorageName,
      JSON.stringify(filter.value),
    )
  } else {
    sessionStorage.setItem(
      toCourseToCourseSearchFilterDataStorageName,
      JSON.stringify(filter.value),
    )
  }
}

const restorePersistence = () => {
  let persistedData = ''
  if (filter.value.fromOrToInstitution === 'from') {
    persistedData = sessionStorage.getItem(fromCourseToCourseSearchFilterDataStorageName)
  } else {
    persistedData = sessionStorage.getItem(toCourseToCourseSearchFilterDataStorageName)
  }
  if (_.isString(persistedData)) {
    persistedData = JSON.parse(persistedData)
  }
  const fromOrToInstitution = _.get(persistedData, 'fromOrToInstitution')
  const isPrivateSelected = _.get(persistedData, 'selectedSecondPrivateInstitutionType')

  if (persistedData) {
    if (!persistedData.selectedSubject || persistedData.selectedSubject.id !== 0) {
      showSubjectValidation.value = false
    } else {
      showSubjectValidation.value = true
    }
  }

  if (!persistedData) {
    if (filter.value.fromOrToInstitution === 'to') {
      filter.value.selectedInstitution = null
      filter.value.selectedSubject = null
      currentPossibleSubjects.value = []
      filter.value.courseNumber = null
      filter.value.courseYear =
        new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
      yearSelectComponentRef.value.setYear(filter.value.courseYear)

      fetchInstitutions(null).then((fetchedInstitutions) => {
        institutions.value = fetchedInstitutions
      })

      secondInstitutionsRef.value.setCountry(filter.value.selectedSecondCountry)

      secondInstitutionsRef.value.setInstitutionTypes(null)
      secondInstitutionsRef.value.setInstitutions(null)
    } else {
      fetchCountries().then((fetchedCountries) => {
        countries.value = fetchedCountries

        filter.value.selectedInstitution = null
        filter.value.selectedSubject = null
        currentPossibleSubjects.value = []
        filter.value.courseNumber = null
        filter.value.courseYear =
          new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
        yearSelectComponentRef.value.setYear(filter.value.courseYear)

        fetchInstitutions(filter.value.selectedCountry.id).then((fetchedInstitutions) => {
          institutions.value = fetchedInstitutions
        })

        secondInstitutionsRef.value.setCountry(
          filter.value.fromOrToInstitution === 'from'
            ? countryBC.value
            : filter.value.selectedSecondCountry,
        )

        secondInstitutionsRef.value.setInstitutionTypes(null)
        secondInstitutionsRef.value.setInstitutions(null)
      })
    }
    return
  }
  if (fromOrToInstitution) {
    _.set(filter.value, 'fromOrToInstitution', fromOrToInstitution)
  }
  _.forEach(
    [
      'selectedCountry',
      'selectedInstitution',
      'selectedSubject',
      'courseNumber',
      'courseYear',
      'selectedSecondInstitutionTypes',
      'selectedSecondPrivateInstitutionType',
      'selectedSecondInstitutions',
      'secondInstitutionsForSenderorReceivers',
      'selectedSecondCountry',
      'selectedCourses',
    ],
    (item) => {
      const value = _.get(persistedData, item)
      _.set(filter.value, item, value)
    },
  )
  const handleSecondInstitutionTypes = () => {
    const secondInstitutionTypes = _.get(persistedData, 'selectedSecondInstitutionTypes')
    secondInstitutionsRef.value
      .setInstitutionTypes(secondInstitutionTypes, isPrivateSelected)
      .then(() => {
        filter.value.selectedSecondInstitutionTypes = secondInstitutionTypes
        handleSecondInstitutions()
      })
  }
  const handleSecondInstitutions = () => {
    const secondInstitutions = _.get(persistedData, 'selectedSecondInstitutions')
    return secondInstitutionsRef.value.setInstitutions(secondInstitutions).then(() => {
      filter.value.selectedSecondInstitutions = secondInstitutions
    })
  }
  const handleSecondCountry = () => {
    const secondCountry = _.get(persistedData, 'selectedSecondCountry')
    return secondInstitutionsRef.value.setCountry(secondCountry).then(() => {
      filter.value.selectedSecondCountry = secondCountry
    })
  }
  Promise.all([handleSecondInstitutions(), handleSecondCountry()]).then(() => {
    submit()
  })
  fetchCountries().then((fetchedCountries) => {
    countries.value = fetchedCountries

    fetchInstitutions().then((fetchedInstitutions) => {
      institutions.value = fetchedInstitutions

      let selectedInstitutionId = _.get(filter.value, 'selectedInstitution.id')
      if (selectedInstitutionId) {
        fetchSubjects(selectedInstitutionId).then((subjects) => {
          currentPossibleSubjects.value = subjects
        })
      }
      handleSecondCountry()
      handleSecondInstitutionTypes()
    })
  })
  const courseYear = _.get(persistedData, 'courseYear')
  yearSelectComponentRef.value.setYear(courseYear)
}

const clearPersistence = () => {
  sessionStorage.removeItem(fromCourseToCourseSearchFilterDataStorageName)
  sessionStorage.removeItem(toCourseToCourseSearchFilterDataStorageName)
}

const newSearch = () => {
  clearPersistence()
  clearStorage()
  window.location.href = props.basePagePath
}

const institutionsChanged = (selectedInstitution) => {
  filter.value.selectedInstitution = selectedInstitution
  filter.value.selectedInstitutionId = _.get(selectedInstitution, 'id')
  filter.value.selectedSubject = null
}

const yearChanged = (year) => {
  filter.value.courseYear = year
}

const secondInstitutionTypesChanged = (selectedInstitutionTypes) => {
  filter.value.selectedSecondInstitutionTypes = selectedInstitutionTypes
}

const secondPrivateInstitutionTypeChanged = (privateInstitutionType) => {
  filter.value.selectedSecondPrivateInstitutionType = privateInstitutionType
}

const secondCountryChanged = (selectedCountry) => {
  filter.value.selectedSecondCountry = selectedCountry
}

const secondInstitutionsChanged = (selectedInstitutions) => {
  filter.value.selectedSecondInstitutions = selectedInstitutions
}

const secondInstitutionsForSenderorReceiversChanged = (institutions) => {
  filter.value.secondInstitutionsForSenderorReceivers = institutions
  if (institutions.length === 0) {
    showValidation.value = true
  } else {
    showValidation.value = false
  }
}

function onSubjectChanged(subject) {
  // Clear validation if subject is changed to anything but 'Any'
  if (!subject || subject.id !== 0) {
    showSubjectValidation.value = false
  } else {
    // If subject is 'Any', keep validation only if it was previously triggered
    // (do nothing, message will show only after Add Course is clicked)
    showSubjectValidation.value = true
  }
}

const addCourse = () => {
  // Show validation only when Add Course is clicked and subject is 'Any'
  // if (filter.value.selectedSubject && filter.value.selectedSubject.id === 0) {
  //   showSubjectValidation.value = true
  //   return
  // }
  // showSubjectValidation.value = false

  const courseYear = _.get(filter.value, 'courseYear', null)
  let institutionBeingAdded = {
    institutionId: _.get(filter.value, 'selectedInstitution.id'),
    institutionCode: _.get(filter.value, 'selectedInstitution.code'),
    subjectId: _.get(filter.value, 'selectedSubject.id'),
    subjectCode: _.get(filter.value, 'selectedSubject.code'),
    courseNumber: _.get(filter.value, 'courseNumber').trim(),
    courseYear: courseYear ? courseYear : null,
  }
  let id = JSON.stringify(institutionBeingAdded)
  let selectedCourses = _.get(filter.value, 'selectedCourses', [])
  const existsAlready = selectedCourses.filter((course) => {
    return _.get(course, 'id') === id
  })
  if (existsAlready.length) {
    return
  }
  _.set(institutionBeingAdded, 'id', id)
  filter.value.selectedCourses.push(institutionBeingAdded)
}

const removeCourse = (courseToRemove) => {
  const selectedCourses = filter.value.selectedCourses.filter((course) => {
    return _.get(courseToRemove, 'id') !== _.get(course, 'id')
  })
  if (selectedCourses) {
    filter.value.selectedCourses = selectedCourses
  }
}

const submitSearch = () => {
  sessionStorage.setItem('searchDirection', filter.value.fromOrToInstitution)
  if (!props.isOnResultsPage) {
    updatePersistence()
    window.location.href = props.resultsPagePath
    return
  }
  submit()
}

const submit = () => {
  secondInstitutionsRef.value.setInstitutionCountryChange(filter.value.selectedSecondCountry.id)
  emit('onSubmit', generateSubmitData(filter.value))
}

const generateSubmitData = (filter) => {
  let courseNumber = _.get(filter, 'courseNumber')
  if (courseNumber) {
    courseNumber = courseNumber.trim()
  }
  return {
    fromOrToInstitution: _.get(filter, 'fromOrToInstitution'),
    first: {
      institution: _.get(filter, 'selectedInstitution'),
      subject: _.get(filter, 'selectedSubject'),
      courseNumber: courseNumber,
      year: _.get(filter, 'courseYear'),
      selectedCourses: _.get(filter, 'selectedCourses'),
    },
    second: {
      institutionTypes: _.get(filter, 'selectedSecondInstitutionTypes'),
      institutions: _.get(filter, 'selectedSecondInstitutions'),
      institutionsForSenderorReceivers: _.get(filter, 'secondInstitutionsForSenderorReceivers'),
      country: _.get(filter, 'selectedSecondCountry'),
      isPrivate: _.get(filter, 'selectedSecondPrivateInstitutionType'),
    },
  }
}

const getSubmitData = () => {
  return generateSubmitData(filter.value)
}

onMounted(() => {
  if (props.isOnResultsPage && !areDetailsParamsSet()) {
    document.body.classList.add('js-is-searching')
    restorePersistence()
    return
  } else {
    if (urlParams) {
      fetchCountries()
        .then((fetchedCountries) => {
          countries.value = fetchedCountries
          return fetchInstitutions()
        })
        .then((fetchedInstitutions) => {
          institutions.value = fetchedInstitutions
        })
        .then(() => {
          restorePersistence()
        })
        .catch((error) => {
          console.error('Error fetching data:', error)
        })
    } else {
      restorePersistence()
    }
  }

  // tippy(tooltip.value, {
  //   content: tooltip.value.getAttribute('data-tooltip'),
  //   placement: 'top',
  // })
})

defineExpose({
  getSubmitData,
  newSearch,
  removeInstitutionTag,
})
</script>

<style>
.course-finder-hidden {
  margin: 20px 0;
}

.m-field-group--inline {
  margin: 20px 0;
}

h4.from-to-institution-filters {
  margin-bottom: 0px;
}

fieldset.from-to-institution-country-filter {
  margin-top: 0px;
}

.filters {
  margin-top: 0px !important;
}

.disabled-country-input:disabled {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  background-color: #d2d2d2 !important;
}

img.option__image {
  margin-right: 7px;
}

.c2c-add-course-tooltip,
div.c2c-add-course-tooltip[data-tooltip] {
  margin-right: 8px !important;
  cursor: pointer;
  position: relative !important;
}
</style>
